import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  MenuItem,
  Button,
  Grid,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
  Chip,
  FormHelperText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';
import userService from '../services/userService';

interface FormOptions {
  genderOptions: string[];
  yearInSchoolOptions: string[];
  raceOptions: string[];
  identityTypeOptions: string[];
  studyProgramOptions: string[];
  departmentOptions: string[];
  relationOptions: string[];
  countryCodes: Array<{ code: string; country: string; display: string }>;
}

interface EnhancedUserData {
  // Basic Information
  userType: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  
  // Contact Information
  phoneCountryCode: string;
  phoneNumber: string;
  address: string;
  
  // Personal Information
  gender: string;
  dateOfBirth: Date | null;
  race: string;
  
  // Academic/Professional Information
  universityId?: number;
  schoolId: string;
  yearInSchool: string;
  identityType: string;
  program: string;
  department: string;
  assignedProviderId?: number;
  
  // Emergency Contact
  emergencyContactName: string;
  emergencyContactRelation: string;
  emergencyContactPhone: string;
  
  // Photo (future implementation)
  photo?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userUniversityId?: number;
  userType: string;
  universities: any[];
  providers: any[];
}

export const EnhancedAddUserDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  userUniversityId,
  userType,
  universities,
  providers
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [formOptions, setFormOptions] = useState<FormOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [filteredProviders, setFilteredProviders] = useState<any[]>([]);
  
  const [formData, setFormData] = useState<EnhancedUserData>({
    userType: 'student',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneCountryCode: '+1',
    phoneNumber: '',
    address: '',
    gender: '',
    dateOfBirth: null,
    race: '',
    universityId: userUniversityId,
    schoolId: '',
    yearInSchool: '',
    identityType: '',
    program: '',
    department: '',
    assignedProviderId: undefined,
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: ''
  });

  const steps = ['Basic Info', 'Personal Details', 'Academic/Professional', 'Emergency Contact'];

  useEffect(() => {
    loadFormOptions();
  }, []);

  useEffect(() => {
    // Filter providers based on selected university
    if (formData.universityId) {
      const universityProviders = providers.filter(p => p.universityId === formData.universityId);
      setFilteredProviders(universityProviders);
    }
  }, [formData.universityId, providers]);

  const loadFormOptions = async () => {
    try {
      const options = await userService.getFormOptions();
      setFormOptions(options);
    } catch (error) {
      console.error('Error loading form options:', error);
      toast.error('Failed to load form options');
    }
  };

  const handleInputChange = (field: keyof EnhancedUserData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Basic Info
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return false;
        }
        if (formData.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          return false;
        }
        return true;
        
      case 1: // Personal Details
        if (!formData.phoneNumber || !formData.gender || !formData.dateOfBirth) {
          toast.error('Please fill in all required personal information');
          return false;
        }
        // Validate phone number format
        const phoneRegex = /^\d{3}-?\d{3}-?\d{4}$/;
        if (!phoneRegex.test(formData.phoneNumber)) {
          toast.error('Please enter a valid phone number (e.g., 555-555-5555)');
          return false;
        }
        // Validate date of birth
        const today = new Date();
        const birthDate = new Date(formData.dateOfBirth);
        const age = today.getFullYear() - birthDate.getFullYear();
        if (birthDate > today) {
          toast.error('Date of birth cannot be in the future');
          return false;
        }
        if (age > 120 || age < 10) {
          toast.error('Please enter a valid date of birth');
          return false;
        }
        return true;
        
      case 2: // Academic/Professional
        if (formData.userType === 'student' && !formData.schoolId) {
          toast.error('School ID is required for students');
          return false;
        }
        return true;
        
      case 3: // Emergency Contact
        if (formData.emergencyContactName && !formData.emergencyContactPhone) {
          toast.error('Emergency contact phone is required when name is provided');
          return false;
        }
        if (formData.emergencyContactPhone && !formData.emergencyContactName) {
          toast.error('Emergency contact name is required when phone is provided');
          return false;
        }
        return true;
        
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    // Validate all steps
    for (let i = 0; i <= 3; i++) {
      if (!validateStep(i)) {
        setActiveStep(i);
        return;
      }
    }

    setLoading(true);
    try {
      // Remove confirmPassword before sending and handle null dateOfBirth
      const { confirmPassword, dateOfBirth, ...restData } = formData;
      const submitData = {
        ...restData,
        dateOfBirth: dateOfBirth || undefined
      };
      
      await userService.createUser(submitData);
      toast.success('User created successfully');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      userType: 'student',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phoneCountryCode: '+1',
      phoneNumber: '',
      address: '',
      gender: '',
      dateOfBirth: null,
      race: '',
      universityId: userUniversityId,
      schoolId: '',
      yearInSchool: '',
      identityType: '',
      program: '',
      department: '',
      assignedProviderId: undefined,
      emergencyContactName: '',
      emergencyContactRelation: '',
      emergencyContactPhone: ''
    });
    onClose();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0: // Basic Information
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="User Type"
                value={formData.userType}
                onChange={(e) => handleInputChange('userType', e.target.value)}
                required
              >
                <MenuItem value="student">Student</MenuItem>
                <MenuItem value="provider">Provider</MenuItem>
                <MenuItem value="admin">Administrator</MenuItem>
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                helperText="This will be used for login"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                helperText="Minimum 8 characters"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                required
                error={formData.confirmPassword !== '' && formData.password !== formData.confirmPassword}
                helperText={
                  formData.confirmPassword !== '' && formData.password !== formData.confirmPassword 
                    ? "Passwords don't match" 
                    : ""
                }
              />
            </Grid>
          </Grid>
        );
        
      case 1: // Personal Details
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Country Code"
                value={formData.phoneCountryCode}
                onChange={(e) => handleInputChange('phoneCountryCode', e.target.value)}
              >
                {formOptions?.countryCodes.map((cc) => (
                  <MenuItem key={cc.code} value={cc.code}>
                    {cc.display}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                placeholder="555-555-5555"
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                multiline
                rows={2}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Gender"
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                required
              >
                {formOptions?.genderOptions.map((gender) => (
                  <MenuItem key={gender} value={gender}>
                    {gender}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Birth"
                  value={formData.dateOfBirth}
                  onChange={(date) => handleInputChange('dateOfBirth', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                  maxDate={new Date()}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Race/Ethnicity"
                value={formData.race}
                onChange={(e) => handleInputChange('race', e.target.value)}
              >
                <MenuItem value="">Prefer not to say</MenuItem>
                {formOptions?.raceOptions.map((race) => (
                  <MenuItem key={race} value={race}>
                    {race}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Identity Type"
                value={formData.identityType}
                onChange={(e) => handleInputChange('identityType', e.target.value)}
              >
                <MenuItem value="">Select Identity</MenuItem>
                {formOptions?.identityTypeOptions.map((identity) => (
                  <MenuItem key={identity} value={identity}>
                    {identity}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        );
        
      case 2: // Academic/Professional
        return (
          <Grid container spacing={2}>
            {userType === 'admin' && universities.length > 1 && (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="University"
                  value={formData.universityId || ''}
                  onChange={(e) => handleInputChange('universityId', Number(e.target.value))}
                  required
                >
                  {universities.map((uni) => (
                    <MenuItem key={uni.id} value={uni.id}>
                      {uni.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            
            {formData.userType === 'student' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="School ID"
                    value={formData.schoolId}
                    onChange={(e) => handleInputChange('schoolId', e.target.value)}
                    required
                    helperText="Student identification number"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Year in School"
                    value={formData.yearInSchool}
                    onChange={(e) => handleInputChange('yearInSchool', e.target.value)}
                  >
                    <MenuItem value="">Select Year</MenuItem>
                    {formOptions?.yearInSchoolOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Study Program"
                    value={formData.program}
                    onChange={(e) => handleInputChange('program', e.target.value)}
                  >
                    <MenuItem value="">Select Program</MenuItem>
                    {formOptions?.studyProgramOptions.map((program) => (
                      <MenuItem key={program} value={program}>
                        {program}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Assigned Provider"
                    value={formData.assignedProviderId || ''}
                    onChange={(e) => handleInputChange('assignedProviderId', Number(e.target.value) || undefined)}
                  >
                    <MenuItem value="">No Provider Assigned</MenuItem>
                    {filteredProviders.map((provider) => (
                      <MenuItem key={provider.userId} value={provider.userId}>
                        {provider.firstName} {provider.lastName}
                      </MenuItem>
                    ))}
                  </TextField>
                  {filteredProviders.length === 0 && formData.universityId && (
                    <FormHelperText>No providers available for selected university</FormHelperText>
                  )}
                </Grid>
              </>
            )}
            
            {formData.userType === 'provider' && (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Department"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                >
                  <MenuItem value="">Select Department</MenuItem>
                  {formOptions?.departmentOptions.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>
        );
        
      case 3: // Emergency Contact
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Emergency contact information is recommended for all users
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                value={formData.emergencyContactName}
                onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Relationship"
                value={formData.emergencyContactRelation}
                onChange={(e) => handleInputChange('emergencyContactRelation', e.target.value)}
                disabled={!formData.emergencyContactName}
              >
                <MenuItem value="">Select Relationship</MenuItem>
                {formOptions?.relationOptions.map((relation) => (
                  <MenuItem key={relation} value={relation}>
                    {relation}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                value={formData.emergencyContactPhone}
                onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                placeholder="555-555-5555"
                disabled={!formData.emergencyContactName}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Summary
                </Typography>
                <Typography variant="body2">
                  Name: {formData.firstName} {formData.lastName}
                </Typography>
                <Typography variant="body2">
                  Email: {formData.email}
                </Typography>
                <Typography variant="body2">
                  Phone: {formData.phoneCountryCode} {formData.phoneNumber}
                </Typography>
                <Typography variant="body2">
                  User Type: {formData.userType}
                </Typography>
                {formData.userType === 'student' && (
                  <>
                    <Typography variant="body2">
                      School ID: {formData.schoolId}
                    </Typography>
                    <Typography variant="body2">
                      Program: {formData.program || 'Not specified'}
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        );
        
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Add New User
        <Typography variant="body2" color="textSecondary">
          Complete all steps to create a new user account
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ width: '100%', mt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          <Box sx={{ mt: 4 }}>
            {renderStepContent(activeStep)}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          disabled={activeStep === 0 || loading}
          onClick={handleBack}
        >
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create User'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};